import {RowDropZoneParams} from "@ag-grid-community/core";
import {SizeUnit} from "react-spaces/dist/core-types";
import {SearchableSortDirection} from "../generated/graphql";
import _, {isArray, isEqual} from "lodash";
import {
  ManifestFilterState,
  ManifestFilterType
} from "../components/manifest/list/components/manifest-filter/ManifestFilterState";
import {DateRange} from "@blueprintjs/datetime";
import {TManifestEnhanced} from "../components/common/types";

type ManifestSortOptions = {
  property: string;
  sortDirection: SearchableSortDirection;
};

type AssignmentViewState = {
  manifestFilters: ManifestFilterState;
  manifestQuery?: string;
  manifestSort: ManifestSortOptions;
  dragAndDropTargets: DragAndDropTargetPayload[];
  currentDragAndDropTargets: RowDropZoneParams[];
  showingJobIds: number[];
  totalJobIds: number;
  unassignedJobsPage: number;
  assignmentTotalJobsNumbers: string[];
  totalJobsNumberAndJobsIdOnPages: {jobId: number; jobNumber: string}[];
  sortByColor: {
    enable: boolean;
    direction: SearchableSortDirection;
  };
};

type DragAndDropTargetPayload = {
  element: HTMLElement;
  manifest: TManifestEnhanced;
};

export type ManifestFilterTypePayload = {
  filterType: ManifestFilterType;
  value: string | number | Date | DateRange | number[] | undefined;
};

type SetShowingJobIdsPayload = {
  jobIds: number[];
  total: number;
  jobIdsAndJobNumbers: {jobId: number; jobNumber: string}[];
};

export enum AssignmentViewActionTypes {
  SET_JOB_PANEL_SIZE = "SetJobPanelSize",
  SET_MANIFEST_PANEL_SIZE = "SetManifestPanelSize",
  SET_MANIFEST_SORT = "SetManifestSort",
  ADD_MANIFEST_FILTER = "AddManifestFilter",
  REMOVE_MANIFEST_FILTER = "RemoveManifestFilter",
  REPLACE_MANIFEST_FILTER_VALUE = "ReplaceManifestFilterValue",
  REPLACE_MANIFEST_FILTER_VALUES = "ReplaceManifestFilterValues",
  CLEAR_MANIFEST_FILTER = "ClearManifestFilter",
  SET_DRAG_AND_DROP_TARGETS = "SetDragAndDropTargets",
  SET_CURRENT_DRAG_AND_DROP_TARGETS = "SetCurrentDragAndDropTargets",
  SET_MANIFEST_QUERY = "SetManifestQuery",
  SET_SHOWING_JOBIDS = "SetShowingJobIds",
  SET_UNASSIGNED_JOBS_PAGE = "SetUnassignedJobsPage",
  SET_JOBS_NUMBER_ON_ALL_ASSIGNMENTS = "SetJobsNumberOnAllAssignments",
  SET_CURRENT_JOBS_IDS_AND_NUMBERS_ON_PAGES = "SetCurrentJobsIdsAndNumbersOnPages",
  SET_SORT_BY_COLOR = "SetSortByColor"
}

type AssignmentViewActions =
  | {type: AssignmentViewActionTypes.SET_JOB_PANEL_SIZE; payload: SizeUnit}
  | {type: AssignmentViewActionTypes.SET_MANIFEST_PANEL_SIZE; payload: SizeUnit}
  | {type: AssignmentViewActionTypes.SET_MANIFEST_SORT; payload: ManifestSortOptions}
  | {
      type: AssignmentViewActionTypes.ADD_MANIFEST_FILTER;
      payload: ManifestFilterTypePayload | ManifestFilterTypePayload[];
    }
  | {type: AssignmentViewActionTypes.REMOVE_MANIFEST_FILTER; payload: ManifestFilterTypePayload}
  | {type: AssignmentViewActionTypes.REPLACE_MANIFEST_FILTER_VALUE; payload: ManifestFilterTypePayload}
  | {
      type: AssignmentViewActionTypes.REPLACE_MANIFEST_FILTER_VALUES;
      payload: ManifestFilterTypePayload;
    }
  | {type: AssignmentViewActionTypes.CLEAR_MANIFEST_FILTER; payload: ManifestFilterType | ManifestFilterType[]}
  | {type: AssignmentViewActionTypes.SET_DRAG_AND_DROP_TARGETS; payload: DragAndDropTargetPayload[]}
  | {type: AssignmentViewActionTypes.SET_CURRENT_DRAG_AND_DROP_TARGETS; payload: RowDropZoneParams[]}
  | {type: AssignmentViewActionTypes.SET_MANIFEST_QUERY; payload: string}
  | {type: AssignmentViewActionTypes.SET_SHOWING_JOBIDS; payload: SetShowingJobIdsPayload}
  | {type: AssignmentViewActionTypes.SET_UNASSIGNED_JOBS_PAGE; payload: number}
  | {type: AssignmentViewActionTypes.SET_JOBS_NUMBER_ON_ALL_ASSIGNMENTS; payload: string}
  | {
      type: AssignmentViewActionTypes.SET_SORT_BY_COLOR;
      payload: {
        enable: boolean;
        direction: SearchableSortDirection;
      };
    };

const AssignmentViewReducer = (state: AssignmentViewState, action: AssignmentViewActions): AssignmentViewState => {
  switch (action.type) {
    case AssignmentViewActionTypes.SET_MANIFEST_SORT: {
      return {
        ...state,
        manifestSort: action.payload
      };
    }

    case AssignmentViewActionTypes.ADD_MANIFEST_FILTER: {
      if (Array.isArray(action.payload)) {
        return {
          ...state,
          manifestFilters: state.manifestFilters.addManifestFilterValues(action.payload)
        };
      }
      return {
        ...state,
        manifestFilters: state.manifestFilters.addManifestFilterValue(action.payload.value, action.payload.filterType)
      };
    }
    case AssignmentViewActionTypes.REMOVE_MANIFEST_FILTER: {
      return {
        ...state,
        manifestFilters: state.manifestFilters.removeManifestFilterValue(
          action.payload.value,
          action.payload.filterType
        )
      };
    }
    case AssignmentViewActionTypes.CLEAR_MANIFEST_FILTER: {
      if (Array.isArray(action.payload)) {
        return {
          ...state,
          manifestFilters: state.manifestFilters.clearManifestFilterTypes(action.payload)
        };
      }
      return {
        ...state,
        manifestFilters: state.manifestFilters.clearManifestFilterType(action.payload)
      };
    }
    case AssignmentViewActionTypes.REPLACE_MANIFEST_FILTER_VALUE: {
      return {
        ...state,
        manifestFilters: state.manifestFilters.replaceManifestFilterValue(
          action.payload.value,
          action.payload.filterType
        )
      };
    }
    case AssignmentViewActionTypes.REPLACE_MANIFEST_FILTER_VALUES: {
      return {
        ...state,
        manifestFilters: state.manifestFilters.replaceManifestFilterValues(
          isArray(action.payload.value) ? action.payload.value : [action.payload.value],
          action.payload.filterType
        )
      };
    }
    case AssignmentViewActionTypes.SET_DRAG_AND_DROP_TARGETS: {
      return {
        ...state,
        dragAndDropTargets: action.payload
      };
    }
    case AssignmentViewActionTypes.SET_CURRENT_DRAG_AND_DROP_TARGETS: {
      if (!_.isEqual(JSON.stringify(state.currentDragAndDropTargets), JSON.stringify(action.payload))) {
        return {
          ...state,
          currentDragAndDropTargets: action.payload
        };
      } else {
        return state;
      }
    }
    case AssignmentViewActionTypes.SET_MANIFEST_QUERY: {
      return {
        ...state,
        manifestQuery: action.payload
      };
    }
    case AssignmentViewActionTypes.SET_SHOWING_JOBIDS: {
      if (
        isEqual(state.showingJobIds, action.payload.jobIds) &&
        isEqual(state.totalJobsNumberAndJobsIdOnPages, action.payload.jobIdsAndJobNumbers)
      ) {
        return state;
      }

      return {
        ...state,
        showingJobIds: action.payload.jobIds,
        totalJobIds: action.payload.total,
        ...(action.payload.jobIdsAndJobNumbers.length > 0
          ? {totalJobsNumberAndJobsIdOnPages: action.payload.jobIdsAndJobNumbers}
          : {totalJobsNumberAndJobsIdOnPages: state.totalJobsNumberAndJobsIdOnPages})
      };
    }

    case AssignmentViewActionTypes.SET_UNASSIGNED_JOBS_PAGE: {
      return {
        ...state,
        unassignedJobsPage: action.payload
      };
    }

    case AssignmentViewActionTypes.SET_JOBS_NUMBER_ON_ALL_ASSIGNMENTS: {
      if (state.assignmentTotalJobsNumbers.includes(action.payload)) {
        const remainingJobsNumber = state.assignmentTotalJobsNumbers
          .slice()
          .filter((jobNumber) => jobNumber !== action.payload);

        return {
          ...state,
          assignmentTotalJobsNumbers: remainingJobsNumber
        };
      } else {
        const newJobsNumber = [...state.assignmentTotalJobsNumbers, action.payload];

        return {
          ...state,
          assignmentTotalJobsNumbers: newJobsNumber
        };
      }
    }

    case AssignmentViewActionTypes.SET_SORT_BY_COLOR: {
      return {
        ...state,
        sortByColor: action.payload
      };
    }
    default:
      return state;
  }
};

export default AssignmentViewReducer;
export type {AssignmentViewActions, AssignmentViewState, ManifestSortOptions, DragAndDropTargetPayload};
